import { ReactElement, createContext, useEffect, useReducer } from "react";
import {
  signIn,
  signUp,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  confirmSignUp,
  resendSignUpCode,
  fetchAuthSession,
  confirmSignIn,
  updateUserAttributes,
  updatePassword,
} from "aws-amplify/auth";

import accountReducer from "store/accountReducer";
import { LOGIN, LOGOUT, SET_APP_USER, SET_APP_USER_LOADING } from "store/actions";
import Loader from "ui-component/Loader";
import { AWSCognitoContextType, InitialLoginContextProps, LoginResult } from "types/auth";
import { getUser, updateUser } from 'api/clientApi';
import { getHighestRoleLabel } from '../constants/roles';

const initialState: InitialLoginContextProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  appUser: null,
  isHydratingAppUser: false
};

const AWSCognitoContext = createContext<AWSCognitoContextType | null>(null);

function setSessionTokens(accessToken?: string | null, idToken?: string | null) {
  if (accessToken) localStorage.setItem("accessToken", accessToken);
  else localStorage.removeItem("accessToken");

  if (idToken) localStorage.setItem("idToken", idToken);
  else localStorage.removeItem("idToken");
}

export function AWSCognitoProvider({ children }: { children: ReactElement }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);



  const refreshUser = async () => {
    // 1) Check if we have tokens (signed in). If not, just log out state cleanly.
    const session = await fetchAuthSession(); // <-- no forceRefresh
    const hasTokens = !!session.tokens?.accessToken;

    if (!hasTokens) {
      setSessionTokens(null, null);
      dispatch({ type: LOGOUT });
      return;
    }

    // 2) Now it's safe to call authenticated endpoints
    await getCurrentUser();
    const attrs = await fetchUserAttributes();
    console.log(attrs);





    //const idPayload = session.tokens?.idToken?.payload ?? {};
    //const accessPayload = session.tokens?.accessToken?.payload ?? {};
    //const groups = accessPayload['cognito:groups'] || idPayload['cognito:groups'] || [];

    const a = attrs as Record<string, string>;
    const givenName = (a['given_name'] ?? '').trim();
    const familyName = (a['family_name'] ?? '').trim();

    const displayName =
      `${givenName} ${familyName}`.trim() ||
      givenName ||
      familyName ||
      (a['email'] ?? '').trim() ||
      'User';

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: { email: a.email, name: displayName }
      }
    });

    dispatch({ type: SET_APP_USER_LOADING });

    // 3) Authenticated Lambda call (only after we know we’re signed in). Kept in its own
    // try/catch: this is a backend profile fetch, not a Cognito session check — a transient
    // failure here (cold start, network blip) must not log out an otherwise-valid session.
    try {
      const profile = await getUser({
        //userName: displayName,
        //phone: '0000000000',
        //email: a.email
      });
      const roleLabel = getHighestRoleLabel(profile?.roles);
      console.log(profile);
      dispatch({
        type: SET_APP_USER,
        payload: {
          appUser: {
            userId: profile?.userId ?? '',
            //orgId: profile?.orgId ?? '',
            orgName: profile?.orgName ?? '',
            roles: profile?.roles ?? [],
            primaryRole: roleLabel,
            firstName: profile?.firstName ?? '',
            lastName: profile?.lastName ?? '',
            email: profile?.email ?? '',
            phone: profile?.phone ?? '',
            avatar: profile?.avatar ?? undefined,
            isInvite: profile?.isInvite ?? false,
            isMember: profile?.isMember ?? false,
            isArchived: profile?.isArchived ?? false,
            isBlocked: profile?.isBlocked ?? false,
            isSelf: true,
            addedTimeStamp: profile?.addedTimeStamp ?? Date.now()
          }
        }
      });
    } catch (err) {
      console.error('Failed to load app user profile (session remains valid):', err);
      dispatch({ type: SET_APP_USER, payload: { appUser: null } });
    }
  };

  useEffect(() => {
    refreshUser().catch((err) => {
      // Only log unexpected errors; unauthenticated is handled above
      console.error('refreshUser failed:', err);
      setSessionTokens(null, null);
      dispatch({ type: LOGOUT });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const res = await signIn({ username: email, password });
      console.log(res);
      const step = res?.nextStep?.signInStep;
      if (step === 'CONFIRM_SIGN_UP') {
        throw new Error('Your account isn’t confirmed yet. Please check your email and click verify.');
      }
      await refreshUser();
      return res;
    } catch (e: any) {
      //console.log(e);
      if (e?.message?.includes('already a signed in user')) {
        await logout();
      }
      throw e;
    }
  };
  /*
    const completeNewPassword = async (newPassword: string): Promise<void> => {
    try {
      const res = await confirmSignIn({
        challengeResponse: newPassword
      });
  
      console.log(res);
  
      const step = res?.nextStep?.signInStep;
      if (step && step !== 'DONE') {
        throw new Error(`Additional sign-in step required: ${step}`);
      }
  
      await refreshUser();
    } catch (e) {
      throw e;
    }
  };
  */

  const completeFirstLoginProfile = async (
    newPassword: string,
    phoneNumber: string,
    firstName: string,
    lastName: string
  ): Promise<void> => {
    try {
      // given_name and family_name must go IN the challenge response, not after it. They are
      // REQUIRED attributes on the user pool (matching serverless.yml and prod), and
      // addOrgMember creates invited users with only `email` — so Cognito rejects the
      // NEW_PASSWORD_REQUIRED challenge itself with "Invalid attributes given, given_name is
      // missing" unless they are supplied here. Setting them afterwards via
      // updateUserAttributes never ran, because the challenge failed first.
      const res = await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes: {
            given_name: firstName.trim(),
            family_name: lastName.trim(),
          }
        }
      });

      console.log(res);

      const step = res?.nextStep?.signInStep;
      if (step && step !== 'DONE') {
        throw new Error(`Additional sign-in step required: ${step}`);
      }

      // phone_number is NOT required on the pool, so it stays here rather than in the challenge —
      // and it is optional in the form, so an empty value must not be written.
      if (phoneNumber?.trim()) {
        await updateUserAttributes({
          userAttributes: { phone_number: phoneNumber.trim() }
        });
      }

      await refreshUser();
    } catch (e) {
      throw e;
    }
  };


  /*
    const refreshUser = async () => {
      const cu = await getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh: true });
      const attrs = await fetchUserAttributes();
      const a = attrs as Record<string, string>;
      const displayName =
        (a['custom:name'] ?? '').trim() ||
        (a['given_name'] ?? '').trim() ||
        (a['email'] ?? '').trim() ||
        'User';
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: { email: a.email, name: displayName }
        }
      });
      dispatch({ type: SET_APP_USER_LOADING });
      const profile = await getUser({ userName: displayName, phone: "0000000000", email: a.email }); // ← Lambda call
      dispatch({
        type: SET_APP_USER,
        payload: {
          appUser: {
            userId: profile?.userId ?? '',
            orgId: profile?.orgId ?? '',
            orgName: profile?.orgName ?? '',
            roles: profile?.roles ?? [],
            name: profile?.name ?? '',
            email: profile?.email ?? '',
            phone: profile?.phone ?? '',
            avatar: profile?.avatar ?? undefined
          }
        }
      });
    };
  
    useEffect(() => {
      refreshUser().catch(() => {
        setSessionTokens(null, null);
        dispatch({ type: LOGOUT });
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
    const login = async (email: string, password: string): Promise<void> => {
      await signIn({ username: email, password });
      await refreshUser();
    };
  */
  const register = async (email: string, password: string, firstName: string, lastName: string): Promise<void> => {
    await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          'custom:name': `${firstName} ${lastName}`
          // phone_number is optional in the Cognito schema and isn't collected on this form —
          // leave it unset rather than writing a fake placeholder value.
        }
      }
    });
  }

  /*
  const logout = async () => {
    await signOut();
    setSessionTokens(null, null);
    dispatch({ type: LOGOUT });
  };
  */

  const logout = async (): Promise<void> => {
    try {
      // clear app state immediately so UI updates now
      setSessionTokens(null, null);
      dispatch({ type: LOGOUT });
    } finally {
      // best-effort sign out (even if it fails, you're logged out locally)
      try {
        await signOut();
      } catch (e) {
        console.error('signOut failed', e);
      }
    }
  };

  const forgotPassword = async (email: string): Promise<void> => {
    await resetPassword({ username: email });
    localStorage.setItem("email", email);
  };

  const awsResetPassword = async (verificationCode: string, newPassword: string): Promise<void> => {
    const email = localStorage.getItem("email");
    if (!email) throw new Error("Missing email in localStorage");
    await confirmResetPassword({ username: email, confirmationCode: verificationCode, newPassword });
    localStorage.removeItem("email");
  };

  const codeVerification = async (verificationCode: string): Promise<void> => {
    const email = localStorage.getItem("email");
    if (!email) throw new Error("Missing email in localStorage");
    await confirmSignUp({ username: email, confirmationCode: verificationCode });
    localStorage.removeItem("email");
  };

  const resendConfirmationCode = async (): Promise<void> => {
    const email = localStorage.getItem("email");
    if (!email) throw new Error("Missing email in localStorage");
    await resendSignUpCode({ username: email });
  };

  const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
    await updatePassword({ oldPassword, newPassword });
  };

  const updateProfile = async (updateType: string, payload: {userId: string | '', firstName: string, lastName: string, phone: string, avatar: string | null}): Promise<void> => {
    await updateUserAttributes({
      userAttributes: {
        given_name: payload.firstName.trim(),
        family_name: payload.lastName.trim(),
        phone_number: payload.phone.trim()
      }
    });
    await updateUser({updateType: updateType, userId: payload.userId, firstName: payload.firstName.trim(), lastName: payload.lastName.trim(), phone: payload.phone.trim(), avatar: payload.avatar ?? null });
    await refreshUser();
  };

  if (!state.isInitialized) return <Loader />;

  return (
    <AWSCognitoContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        forgotPassword,
        awsResetPassword,
        updateProfile,
        changePassword,
        codeVerification,
        resendConfirmationCode,
        completeFirstLoginProfile,
        refreshUser
      }}
    >
      {children}
    </AWSCognitoContext.Provider>
  );
}

export default AWSCognitoContext;